import React from "react";
import NP from "number-precision";
import RollTableProps from "./rollTableProps";
import StandardGrid from "common/agGrid/StandardGrid";
import useColumnDefinitions from "common/agGrid/useColumnDefinitions";
import { ColumnBuilder } from "common/agGrid/columnBuilder";
import { ContractRow } from "./contractRow";
import { Form } from "react-bootstrap";
import { getProducerDisplayName } from "features/settings/producers/producer";
import { formatDecimalPlace, formatQuantity, isErpIdSyncing } from "common/formatters";
import ChangedValue from "./ChangedValue";
import NumberEditor from "common/ui/NumberEditor";

const columnBuilder = new ColumnBuilder<ContractRow>();

const RollBasisTable: React.FC<RollTableProps> = React.memo(props => {

    const areAllSelected = props.rows.length > 0 && props.rows.every(m => m.isSelected);

    const colDefs = useColumnDefinitions(() => {
        const cols = [
            columnBuilder.for("", "isSelected", { hide: props.hideRowSelection })
                .withSelectAllHeader({ isSelected: areAllSelected, onChange: props.onSelectAll })
                .withCellRenderer(params =>
                    <Form.Check checked={params.value} onChange={ev => props.onRowSelected(params.node.data, ev.target.checked)} />)
                .withWidth(40),
            columnBuilder.for("ID", "displayId")
                .withWidth(80),
            columnBuilder.for("ERP ID", "erpDisplayId", { hide: !props.showErp })
                .withCellClassRules({ "text-danger": params => isErpIdSyncing(params.value) }),
            columnBuilder.for("Customer", "")
                .withGetter(params => getProducerDisplayName(params.data?.producer))
                .withWidth(140),
            columnBuilder.for("Unpriced", "remainingQuantity")
                .asNumber()
                .withFormatter(formatQuantity)
                .withWidth(100),
            columnBuilder.for("Roll Quantity", "rollQuantity", { hide: !props.allowPartialRoll })
                .withCellClass("editable-cell")
                .withCellRenderer(params =>
                    <NumberEditor
                        value={params.value ?? params.node.data.remainingQuantity}
                        onValueChange={value => props.onRollQuantityChanged?.(params.node.data, value ?? params.node.data.remainingQuantity)}
                        min={0}
                        max={params.node.data.remainingQuantity}
                        step={1}
                        allowDecimals
                        enforcePrecision
                    />
                )
                .withWidth(120),
            columnBuilder.for("Symbol", "futuresContract.symbol")
                .withCellRenderer(params =>
                    <ChangedValue
                        showChange={params.node.data.isSelected && props.rollTo.futuresContract != null &&
                            params.value !== props.rollTo.futuresContract.symbol}
                        oldValue={params.value}
                        newValue={props.rollTo.futuresContract?.symbol}
                    />)
                .withWidth(100),
            columnBuilder.for("Basis", "basis")
                .withCellRenderer(params => {
                    return <ChangedValue
                        showChange={params.node.data.isSelected && props.spread != null && props.spread !== 0}
                        oldValue={params.value}
                        newValue={NP.plus(params.value, props.spread ?? 0)}
                        formatter={formatDecimalPlace}
                    />
                })
                .asRightAligned()
                .withWidth(100)
        ];
        return cols.map(m => m.build());
    }, [props.rollTo, props.spread, areAllSelected, props.onRowSelected, props.onSelectAll, props.onRollQuantityChanged]);

    return <div className="roll-basis-table">

        <div className="mt-2" >
            <StandardGrid
                paginationPageSize={25}
                pagination
                suppressRowClickSelection
                rowData={props.rows}
                columnDefs={colDefs}
                getRowId={getRowId}
                rowHeight={30} />
        </div>
    </div>;
});

const getRowId = (params: any) => params.data.id;

export default RollBasisTable